import * as React from 'react';

import { cn } from '@repo/ui/lib/utils';
import { LiquidSurface } from './liquid-surface';

interface ChartPanelLegendItemProps {
  color: string;
  label: string;
}

/** A single colored-dot legend entry, e.g. a benchmark series label. */
function ChartPanelLegendItem({ color, label }: ChartPanelLegendItemProps) {
  return (
    <span className="text-rowa-body-sm text-rowa-text-secondary inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

interface ChartPanelProps {
  title: string;
  /** Semantic level for the panel title. Defaults to h3 for nested charts. */
  headingLevel?: 'h2' | 'h3';
  description?: string;
  /** Slot for a `PeriodSelector` or other range control, rendered top-right. */
  periodSelector?: React.ReactNode;
  /** Slot for `ChartPanelLegendItem`s or any custom legend row. */
  legend?: React.ReactNode;
  /** The chart itself — pass the underlying chart component with `noCard`. */
  children: React.ReactNode;
  /** e.g. "Cash-flow-adjusted TWR. Period ends 31 Jul 2026." */
  footnote?: React.ReactNode;
  className?: string;
}

/**
 * Glass-panel wrapper every chart screen (Overview, Portfolio, Vaults,
 * Risk & Liquidity) repeats: title + period-selector slot + optional
 * legend row + the chart + a footnote line. Composes the existing
 * `LineChartComponent`/`MultiLineChartComponent`/`GradientChart`/
 * `PieChartComponent` (rendered with `noCard`) rather than re-implementing
 * charting.
 */
function ChartPanel({
  title,
  headingLevel = 'h3',
  description,
  periodSelector,
  legend,
  children,
  footnote,
  className
}: ChartPanelProps) {
  const Heading = headingLevel;

  return (
    <LiquidSurface type="panel" data-slot="chart-panel" className={cn('p-6', className)}>
      <div className="flex items-start justify-between gap-4">
        {/* min-w-0 + flex-1: the description wraps inside its column instead
            of pushing the selector onto its own row on half-width panels. */}
        <div className="min-w-0 flex-1">
          <Heading className="text-rowa-card text-rowa-text-primary">{title}</Heading>
          {description && (
            <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">{description}</p>
          )}
        </div>
        {periodSelector && <div className="shrink-0">{periodSelector}</div>}
      </div>

      {legend && <div className="mt-4 flex flex-wrap items-center gap-4">{legend}</div>}

      <div className="mt-6">{children}</div>

      {footnote && <p className="text-rowa-body-sm text-rowa-text-secondary mt-4">{footnote}</p>}
    </LiquidSurface>
  );
}

export { ChartPanel, ChartPanelLegendItem };
export type { ChartPanelProps, ChartPanelLegendItemProps };
